/**
 * 拆分与冲销内核 —— **结构性判定的唯一实现**（**C 域 · C2**）
 *
 * 依据：C 域 PRD **§5.2.1**（工资条）· **§5.2.2**（拆分）· **§5.2.3**（退款 = 冲减腿）·
 * **§5.2.4**（手续费独立成笔）· 规格 **§3.8** 复式记账口径铁律 · **§163**（「父行」在 v2 腿模型下的定义）·
 * `DR-016` **§3.7 / §3.8** · `DR-022` v1.1（冲减上限）· 🔴 **`DR-035` v1.1（拆分载体改判）**。
 *
 * ## 🔴 v2 的「父行」是**派生展示行**，不是一条真实记录（`DR-035` v1.1 改判）
 *
 * 规格 **§3.8 第 163 行**（2026-09-25 补的订正条款）明文：
 * 「v1 的"父行"是**一条真实记录**（v1 为单记录模型）；**v2 下 = 该交易在「主账户」上的「分组净额」
 * （派生值，不落库、不新增字段）**」。据此三种形态**同构**：
 *
 * | 形态 | 腿的构成 |
 * |---|---|
 * | **拆分** | **一笔交易 · 多条腿** —— 同账户 / 同向（都 `out`）/ 各带分类 |
 * | **工资条** | **一笔交易 · 多条腿** —— 跨账户 / 有加有减 |
 * | **转账** | 一笔交易 · 2 条腿 · 跨账户 |
 *
 * ⚠️ **为什么不是「1 个容器交易 + N 个子交易」**（⑭ 批的错误读法，本批已改判）：
 * 容器与子项**都带真实腿** ⇒ 余额与统计被**重复计一次**（实测：100 元支出扣了 200 元，
 * 净资产 / 信用卡应还 / 预算已用按余额派生 ⇒ 一起错）。
 * 而「**腿 = 一笔资金变动**」是本项目全部汇总赖以成立的不变量，
 * **不能为了容纳"容器"这个实体而给它加例外**（加 `legs.affectsBalance` 之类的标记会让
 * 余额 / 净资产 / 账单 / 预算 / 报表五处都要跟着分叉）。详见 `DR-035` v1.1 §10 的穷举。
 *
 * ## 本文件负责什么
 *
 * 全部是「**这一组腿构成什么形态**」的问题：是不是退款？多腿的出口唯一吗？转账形态对吗？腿数超限吗？
 * 服务端用它**拦非法写入**，端侧用它**画对应的编辑界面**。若各写一份，
 * 「什么算退款」就会在 Web、小程序、服务端出现三个答案 —— 那正是铁律 24 点名的病。
 */

import type { Cents } from '../types/brand.js';
import { CategoryKind } from '../enums/platform.js';
import type { LegDirection } from '../enums/journal.js';
import { TransactionPolicy } from '../constants/index.js';
import { sumCents } from './money.js';

// ───────────────────────────── 输入 ─────────────────────────────

/**
 * 形态判定所需的一条腿（最小子集）。
 *
 * ⚠️ `categoryKind` 由数据层 join 分类表得到 —— 内核**不能从方向推断分类大类**
 * （收入交易的 `in` 腿也挂分类），故与 `expense.ts` 同约定：保持内核为无外部依赖的纯函数。
 */
export interface ShapeLeg {
  accountId: string;
  direction: LegDirection;
  /** **正**整数分 */
  amount: Cents;
  categoryId: string | null;
  categoryKind: CategoryKind | null;
  /** 腿级「不计收支」 */
  excludeFromStats: boolean;
}

/**
 * 一个交易在形态判定时需要的**全部事实**。
 *
 * 🔴 `parentId` 在 v2 下**只有一种用途 = 退款指向被冲减的原交易**（`DR-035` v1.1 §3）——
 * 它不再表示"父子拆分关系"（拆分已改判为同一笔交易的多腿）。
 */
export interface TxnShape {
  /** 该交易**自身**的 `parentId`（`null` = 顶层交易）；非空 ⇒ 它是一笔**退款** */
  parentId: string | null;
  /** 该交易是否已被软删 */
  deleted: boolean;
  legs: readonly ShapeLeg[];
  /** 交易级「不计收支」 */
  excludeFromStats: boolean;
  /** 是否被判为**退款**（= `isRefundTxn(legs, excludeFromStats)`） */
  refund: boolean;
}

/** 「有效的不计收支」= 腿级 ∨ 交易级（与 `txn-row.isCountedInStats` **同一判据**） */
function effectiveExcluded(leg: ShapeLeg, txnExcludeFromStats: boolean): boolean {
  return leg.excludeFromStats || txnExcludeFromStats;
}

/** 计入收支的腿 */
function countedLegs(legs: readonly ShapeLeg[], txnExcludeFromStats: boolean): ShapeLeg[] {
  return legs.filter((l) => !effectiveExcluded(l, txnExcludeFromStats));
}

// ─────────────────────── 形状谓词（唯一实现） ───────────────────────

/**
 * **是不是退款**（= 冲减腿构成的一笔）。
 *
 * 判据（C 域 PRD §5.2.3 + 规格 §22.9）：
 * 1. 计入收支的腿**非空**（全是排除腿的交易既不是退款也不是支出）；
 * 2. 这些腿**全部是 `in`**（退款是资金回流，❌ 绝不是负数 `out`）；
 * 3. **至少一条挂支出分类**（`in` + 支出分类 = 冲减腿；挂收入分类的 `in` 腿是收入）。
 *
 * ⚠️ **与「报销型理赔」在数据上不可区分**：后者同样是「`in` 腿 + 支出分类」，但 C 域 PRD §5.2.3
 * 说它**可不挂 `parentId`** ⇒ 本谓词**不**用于强制 `parentId`（那会把合法理赔拦掉）。
 * 登记见 `DR-035` v1.1 §5（结论：退款「应挂」不强制，前端引导）。
 */
export function isRefundTxn(legs: readonly ShapeLeg[], txnExcludeFromStats = false): boolean {
  const counted = countedLegs(legs, txnExcludeFromStats);
  if (counted.length === 0) return false;
  if (!counted.every((l) => l.direction === 'in')) return false;
  return counted.some((l) => l.categoryKind === CategoryKind.Expense);
}

/** 父行净腿变动（Σin − Σout，原币整数分） */
export function netOfLegs(legs: readonly ShapeLeg[]): number {
  const ins: Cents[] = [];
  const outs: Cents[] = [];
  for (const l of legs) {
    if (l.direction === 'in') ins.push(l.amount);
    else outs.push(l.amount);
  }
  return (sumCents(ins) as number) - (sumCents(outs) as number);
}

// ───────────────────────── 结构性校验（返回问题清单） ─────────────────────────

/** 工资条的**腿数**上限（子项上限 15 见下方说明）—— `DR-016` §3.8④ */
export const MaxPayrollItems = TransactionPolicy.MaxPayrollItems;
/** **拆分**（同账户同向多腿）的腿数上限 —— C 域 PRD §5.2.2 */
export const MaxSplitChildren = TransactionPolicy.MaxSplitChildren;
/** 复合交易的腿数防御上限（非产品口径） */
export const MaxLegsPerCompositeTxn = TransactionPolicy.MaxLegsPerCompositeTxn;

/**
 * 🔴 **多腿交易的腿数上限**（`DR-035` v1.1 §4 的分派）。
 *
 * 按**账户数**分派口径 —— 这正是拆分与工资条的分界：
 *
 * | 形态 | 判据 | 上限 |
 * |---|---|---|
 * | **拆分** | 全部腿在**同一账户** | `MaxSplitChildren` = **10**（C 域 PRD §5.2.2） |
 * | **工资条 / 复合** | 腿**跨账户** | `MaxPayrollItems` = **15**（`DR-016` §3.8④） |
 * | 防御上限 | —— | `MaxLegsPerCompositeTxn` = 40（非产品口径） |
 *
 * ⚠️ **「子项 15」与「腿数 15」不等价**：工资条里一个"子项"（如社保）可能是 1 条腿（支出）
 * 或 2 条腿（转账到社保账户）。⇒ 本函数按**腿数**拦，且跨账户那一档取宽松值 15；
 * **精确的"子项 ≤ 15"由录入界面的子项行数保证**（属端侧批次，已登记）。
 */
export function checkCompositeShape(legs: readonly ShapeLeg[]): string[] {
  if (legs.length <= 1) return [];
  if (legs.length > MaxLegsPerCompositeTxn) {
    return [
      `一笔交易的腿数上限为 ${MaxLegsPerCompositeTxn} 条（防御值），收到 ${legs.length} 条`,
    ];
  }
  const accounts = new Set(legs.map((l) => l.accountId));
  if (accounts.size === 1 && legs.length > MaxSplitChildren) {
    return [
      `拆分（全部腿在同一账户）的子项上限为 ${MaxSplitChildren} 条，收到 ${legs.length} 条`
        + '（C 域 PRD §5.2.2）',
    ];
  }
  if (accounts.size > 1 && legs.length > MaxPayrollItems) {
    return [
      `跨账户的多腿交易上限为 ${MaxPayrollItems} 条，收到 ${legs.length} 条（DR-016 §3.8④）`,
    ];
  }
  return [];
}

/**
 * 🔴 **资金出口恒为 1 个主账户**（`DR-016` §3.8③ · `AC-C2.20`）。
 *
 * 判据：一笔**多腿**且**有计入收支的腿**的交易，若跨了 ≥2 个账户，
 * 则必须**恰好有 1 个账户同时出现 `in` 与 `out` 腿**（那就是"资金出口"）。
 *
 * - 工资条：工资卡 `in`（税前）+ 多条 `out`（社保 / 个税 / 税后去向）；社保账户**只有 `in`**
 *   ⇒ 唯一命中 ⇒ 通过。
 * - **拆分**：全部腿同账户且同向 ⇒ `byAccount.size === 1` ⇒ 本条不适用（天然通过）。
 * - 「基本工资发 A 卡、绩效发 B 卡」想让两个账户都当出口 ⇒ 两账户同时收付 ⇒ **拦下**
 *   （`DR-016` §3.8⑧：应**录两条工资条**，各带自己的父项）。
 *
 * ⚠️ **只对 ≥3 腿生效**：2 腿的跨账户计入收支交易（报销类）是合法形态，不在本条射程内。
 */
export function checkFundSingleOutlet(legs: readonly ShapeLeg[], txnExcludeFromStats = false): string[] {
  const counted = countedLegs(legs, txnExcludeFromStats);
  if (legs.length < 3 || counted.length === 0) return [];

  const byAccount = new Map<string, { hasIn: boolean; hasOut: boolean }>();
  for (const l of counted) {
    const g = byAccount.get(l.accountId) ?? { hasIn: false, hasOut: false };
    if (l.direction === 'in') g.hasIn = true;
    else g.hasOut = true;
    byAccount.set(l.accountId, g);
  }
  if (byAccount.size < 2) return [];

  const outlets = [...byAccount.values()].filter((g) => g.hasIn && g.hasOut);
  if (outlets.length === 1) return [];
  return [
    outlets.length === 0
      ? '这笔多腿交易找不到唯一的「资金出口」账户（应有一个账户同时收付）—— 请检查各子项的去向'
      : `这笔多腿交易有 ${outlets.length} 个账户同时收付；多腿交易的资金出口**恒为 1 个主账户**（多卡发放请录两条）`,
  ];
}

/**
 * 🔴 **转账形态校验**：全部腿都不计收支 ⇒ 只允许 **1 条（调整类）或 2 条（转账）腿**。
 *
 * 依据 C 域 PRD **§5.2.4**：「手续费 = 一笔**独立支出交易**（1 条 `out` 腿），
 * **不是转账的第 3 条腿**」，并明写「**防复刻者误建 3 腿转账**」。
 * ⇒ 与其等它在报表里表现为"一笔转账 + 一笔不明支出"，不如**写入期拦下并给出正确做法**。
 *
 * ⚠️ 1 腿全排除是合法的（投资买卖 / 「调整」流水 / 分期每期还款都长这样）。
 */
export function checkTransferShape(legs: readonly ShapeLeg[], txnExcludeFromStats = false): string[] {
  if (legs.length === 0) return [];
  const allExcluded = legs.every((l) => effectiveExcluded(l, txnExcludeFromStats));
  if (!allExcluded) return [];
  if (legs.length <= 2) return [];
  return [
    `全部腿都不计收支的交易只允许 1 条（调整类）或 2 条（转账）腿，收到 ${legs.length} 条。`
      + '手续费 / 汇费 / 服务费请**独立记一笔支出**（C 域 PRD §5.2.4：不得作为转账的第 3 条腿）',
  ];
}

/**
 * **多腿交易的综合形态校验** = 上面两条的组合入口。
 *
 * 服务端在写入前调它一次；顺序即报错优先级（腿数 → 出口 → 转账形态）。
 */
export function checkLegShape(legs: readonly ShapeLeg[], txnExcludeFromStats = false): string[] {
  return [
    ...checkCompositeShape(legs),
    ...checkFundSingleOutlet(legs, txnExcludeFromStats),
    ...checkTransferShape(legs, txnExcludeFromStats),
  ];
}

/**
 * 🔴 **退款可不可以挂到某个父交易上**（`DR-035` v1.1 §3 的退款专线）。
 *
 * v2 下 `parentId` 只有退款一个用途 ⇒ 判据收敛为三条：
 * 1. 父必须**存在**且**未被软删**（删优先）；
 * 2. 父必须是**支出**（净腿变动为流出）—— 收入 / 转账不能被冲减；
 * 3. 父**本身不能是退款**（退款不可再被冲减，C 域 PRD §5.2.3「退款不可再被拆分」）。
 *
 * ⚠️ 调用方**还须**自证"本笔是退款形态"（{@link isRefundTxn}）—— 本函数只看父。
 */
export function checkRefundParent(parent: TxnShape): string[] {
  if (parent.deleted) {
    return ['原交易已被删除（删除优先），不能作为退款对象'];
  }
  if (parent.refund) {
    return ['退款不可再被冲减（C 域 PRD §5.2.3）—— 不能把一笔退款当作原交易'];
  }
  if (netOfLegs(parent.legs) >= 0) {
    return ['退款只能冲减**支出**（原交易净额须为流出）；收入 / 转账不能被冲减'];
  }
  return [];
}
